import { useEffect, useState } from 'react';
import Icon from './Icon';
import { ErrorBanner } from './Feedback';
import { api } from '../api/client';

/**
 * The in-app viewer both repositories open resources in.
 *
 * <p>The link is fetched when the viewer opens and is good for minutes, so nothing
 * durable is ever put in the DOM. The watermark comes from the server with the link —
 * the client is told what to overlay rather than composing it, so a page that was
 * screenshotted names whoever was signed in.
 *
 * <p>Three things to open. A document comes from our own media endpoint on a signed
 * link that expires. Video and audio come from Vimeo, where the URL is not a credential
 * at all: it is refused on any page that is not the allowed domain, which is the one
 * thing a forwarded link could never carry with it. An activity is a zip of web files
 * served out of our own storage, page and assets alike, behind one signed link whose
 * token sits in the path so the page's relative links keep it.
 *
 * <p>The activity frame is sandboxed <em>without</em> allow-same-origin, which is not
 * a precaution but the thing that makes the feature safe to have. It is somebody
 * else's HTML, and HTML on this origin can read the localStorage holding the refresh
 * token and call the API as whoever is signed in. Denied a shared origin it can still
 * run its scripts, play its audio and show its images, and can reach nothing here.
 * The server sends the same sandbox as a header, so neither is load-bearing alone.
 */
export default function ResourceViewer({ resource, role, onClose }) {
  const [link, setLink] = useState(null);
  const [error, setError] = useState(null);

  const isStudent = role === 'STUDENT';

  useEffect(() => {
    let cancelled = false;
    setLink(null);
    setError(null);
    // An activity is a VIEW like a document: STREAM is for things with a timeline.
    const request = isStudent
      ? api.studentStreamUrl(resource.resourceId)
      : api.teacherSignedUrl(resource.resourceId, resource.video ? 'STREAM' : 'VIEW');
    request.then((result) => !cancelled && setLink(result)).catch((err) => !cancelled && setError(err));
    return () => {
      cancelled = true;
    };
  }, [resource.resourceId, resource.video, isStudent]);

  async function download() {
    setError(null);
    try {
      const signed = await api.teacherSignedUrl(resource.resourceId, 'DOWNLOAD');
      window.open(signed.url, '_blank', 'noopener');
    } catch (err) {
      setError(err);
    }
  }

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label={resource.name}>
      <div className="viewer">
        <div className="viewer-head">
          <div>
            <h2>{resource.name}</h2>
            <p className="muted small" style={{ margin: '2px 0 0' }}>{resource.typeLabel}</p>
          </div>
          <div className="toolbar">
            {resource.downloadable && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={download}>
                <Icon name="download" size={16} /> Download
              </button>
            )}
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        <div className="viewer-body">
          <ErrorBanner error={error} onDismiss={() => setError(null)} />
          {!link && !error && <div className="empty">Opening…</div>}

          {link && (
            <div className="viewer-stage">
              {link.kind === 'ACTIVITY' ? (
                // Somebody else's HTML, so: no allow-same-origin. That withholds this
                // application's origin from it — no localStorage, no cookies, no
                // same-origin call to the API — while leaving it everything an
                // activity actually uses. allow-scripts and allow-same-origin
                // together would be worse than neither, because a document granted
                // both can take its own sandbox off.
                //
                // allow-popups and allow-top-navigation are deliberately absent: a
                // lesson has nowhere else to send a child.
                <iframe
                  src={link.url}
                  title={resource.name}
                  sandbox="allow-scripts"
                  allow="autoplay; fullscreen"
                  allowFullScreen
                />
              ) : link.provider === 'VIMEO' ? (
                // Vimeo's own player. The parameters that strip the Vimeo chrome are
                // on the URL the server built; what matters here is that the frame is
                // never given more than it needs — no downloads, no clipboard, no
                // camera — and that fullscreen still works, because a lesson watched
                // in a 400px box is not watched.
                <iframe
                  src={link.url}
                  title={resource.name}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              ) : resource.video ? (
                <video
                  src={link.url}
                  controls
                  controlsList="nodownload noplaybackrate"
                  disablePictureInPicture
                  onContextMenu={(event) => event.preventDefault()}
                >
                  <track kind="captions" />
                </video>
              ) : (
                // #toolbar=0 asks the browser's PDF viewer to hide its own controls.
                // It is a UI request, not a security boundary — see the README.
                <iframe src={`${link.url}#toolbar=0&navpanes=0`} title={resource.name} />
              )}
              {/* Lighter over an activity, and only over an activity. The mark is a
                  screenshot deterrent for content worth leaking — a teacher manual, an
                  assessment — and an activity is the least of those: it is sandboxed,
                  it is not downloadable, and a screenshot of it is a screenshot of a
                  drill. What it costs there is real, though, because the thing under
                  it is being read and tapped by a child. So it stays, thinly enough
                  that a screenshot still names who had it open. */}
              <div
                className={`watermark${link.kind === 'ACTIVITY' ? ' watermark-faint' : ''}`}
                aria-hidden="true"
              >
                {Array.from({ length: link.kind === 'ACTIVITY' ? 3 : 8 }, (_, index) => (
                  <span key={index}>{link.watermark}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {link && (
          <div className="viewer-foot">
            <span className="tiny muted">{link.watermark}</span>
            {/* A Vimeo link carries no expiry — the domain whitelist is its boundary,
                not a clock — and the field is omitted rather than null, so this read
                "expires at Invalid Date" on every video. */}
            <span className="tiny muted">
              {link.expiresAt
                ? `This link expires at ${new Date(link.expiresAt).toLocaleTimeString()}`
                : 'This video plays only inside FOCUS.'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
